import React, { useCallback, useRef, useState } from "react";

import moment from "moment";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

import * as Sticker from "ds/Sticker";
import ActivityLogModal from "src/organization/pages/project/environment/metrics/components/ActivityLogModal/ActivityLogModal";
import { EmptyMetrics } from "src/organization/pages/project/environment/metrics/components/Errors";
import HostSelector from "src/organization/pages/project/environment/metrics/components/HostSelector";
import LastUpdated from "src/organization/pages/project/environment/metrics/components/LastUpdated";
import Timeframe from "src/organization/pages/project/environment/metrics/components/TimeframeSelector";
import ToggleActivitiesOverlay from "src/organization/pages/project/environment/metrics/components/ToggleActivitiesOverlay";
import Service from "src/organization/pages/project/environment/metrics/containers/Service";
import useDedicatedWorker from "src/organization/pages/project/environment/metrics/hooks/useDedicatedWorker";
import useDedication from "src/organization/pages/project/environment/metrics/hooks/useDedication";
import useHosts from "src/organization/pages/project/environment/metrics/hooks/useHosts";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import { timeframes } from "src/organization/pages/project/environment/metrics/resources/settings";
import * as V from "src/organization/pages/project/environment/metrics/resources/views.styles";

import * as S from "../Grid/Grid.styles";

import Skeleton from "./Dedication.skeleton";
//TODO rename to dedicated and rename dedicated to dedication
const Dedication = ({ chorusURL, collection }) => {
  const [maxZoom, setMaxZoom] = useState(false);
  const [
    services,
    hosts,
    [firstDataPoint, lastDataPoint],
    selectedTimeframe,
    setSelectedTimeframe,
    isLoading,
    error
  ] = useDedication(chorusURL, collection);

  const dedicationHosts = useHosts(hosts);

  const [selectedHosts, setHosts] = dedicationHosts ?? [undefined, undefined];

  const onBrush = useCallback(
    (from, to) => {
      if (maxZoom) {
        setSelectedTimeframe({
          from: selectedTimeframe.from,
          to: selectedTimeframe.to
        });
        return;
      }
      const selectedRange = moment(to).diff(moment(from), "minutes");
      if (selectedRange < 3) {
        const newEndpoint = moment(from).add(3, "minutes").format();
        setMaxZoom(true);
        setSelectedTimeframe({ from, to: newEndpoint });
      } else {
        setSelectedTimeframe({ from, to });
      }
    },
    [maxZoom, setMaxZoom, setSelectedTimeframe]
  );

  const onTimeframeChange = useCallback(
    timeframe => {
      setMaxZoom(false);
      setSelectedTimeframe(timeframe);
    },
    [setMaxZoom, setSelectedTimeframe]
  );

  const activities = useDedicatedWorker(selectedTimeframe);
  const headerRef = useRef(null);
  useStickyTimecontrol({ ref: headerRef });

  if (error) {
    return (
      <Sticker.Root priority="critical">
        <Sticker.Icon priority="critical" />
        <Sticker.Text>{error?.message}</Sticker.Text>
      </Sticker.Root>
    );
  }

  const showServices = !!services;
  const showEmptyMetrics =
    !isLoading && services && Object.keys(services).length === 0;

  return (
    <>
      <ActivityLogModal />
      <V.Header ref={headerRef}>
        <V.Heading tag="h2">
          <FormattedMessage id="metrics.grid.heading" />
        </V.Heading>
        <V.TimeframeColumn>
          {selectedTimeframe && (
            <LastUpdated
              interval={selectedTimeframe.interval}
              lastDataPoint={lastDataPoint}
              isCustomTimeframe={!selectedTimeframe.id}
            />
          )}
          <V.Row style={{ marginLeft: "auto" }}>
            <ToggleActivitiesOverlay />
            <HostSelector
              hosts={hosts}
              selected={selectedHosts}
              onChange={setHosts}
            />
            <Timeframe
              selectedTimeframe={selectedTimeframe}
              timeframes={timeframes}
              onChange={onTimeframeChange}
              to={lastDataPoint}
              from={firstDataPoint}
            />
          </V.Row>
        </V.TimeframeColumn>
      </V.Header>
      <S.Layout>
        {isLoading && !services && <Skeleton />}
        {showEmptyMetrics && <EmptyMetrics />}
        {showServices &&
          services?.map((service, i) => (
            <Service
              key={i}
              service={service}
              chorusURL={chorusURL}
              selectedTimeframe={selectedTimeframe}
              onBrush={onBrush}
              hosts={selectedHosts}
              isLoading={isLoading}
              activities={activities}
              kind="dedication"
              maxZoom={maxZoom}
            />
          ))}
      </S.Layout>
    </>
  );
};

Dedication.propTypes = {
  chorusURL: PropTypes.string,
  collection: PropTypes.string
};

export default Dedication;
